/**
 * Infrastructure catalog for container deployments
 * Includes PDUs, UPS, cooling, fire suppression, cable management, and monitoring
 */

import { InfrastructureType, InfrastructureSpecs } from '../types/configurator';

export interface InfrastructureCatalogItem {
  id: string;
  type: InfrastructureType;
  name: string;
  description: string;
  specs: InfrastructureSpecs;
  mountType: 'rack' | 'floor' | 'wall' | 'ceiling';
}

export const INFRASTRUCTURE_CATALOG: InfrastructureCatalogItem[] = [
  // ============================================
  // PDUs (Power Distribution Units)
  // ============================================
  {
    id: 'pdu-basic-8',
    type: 'pdu',
    name: 'Basic PDU (8-outlet)',
    description: '8-outlet rack-mount PDU, 15A/120V',
    specs: {
      outlets: 8,
      inputVoltage: 120,
      maxAmps: 15,
      powerWatts: 0,
      rackUnits: 1,
      widthMm: 44,
      depthMm: 356,
      price: 75,
    },
    mountType: 'rack',
  },
  {
    id: 'pdu-metered-16',
    type: 'pdu',
    name: 'Metered PDU (16-outlet)',
    description: '16-outlet metered PDU with LCD display, 20A/208V',
    specs: {
      outlets: 16,
      inputVoltage: 208,
      maxAmps: 20,
      powerWatts: 0,
      rackUnits: 0, // 0U vertical mount
      widthMm: 54,
      depthMm: 1778,
      price: 200,
    },
    mountType: 'rack',
  },
  {
    id: 'pdu-smart-24',
    type: 'pdu',
    name: 'Smart PDU (24-outlet)',
    description: '24-outlet smart PDU with per-outlet monitoring, 30A/208V',
    specs: {
      outlets: 24,
      inputVoltage: 208,
      maxAmps: 30,
      powerWatts: 0,
      rackUnits: 0, // 0U vertical mount
      widthMm: 54,
      depthMm: 1778,
      price: 450,
    },
    mountType: 'rack',
  },
  {
    id: 'pdu-3phase',
    type: 'pdu',
    name: '3-Phase PDU (36-outlet)',
    description: '36-outlet 3-phase PDU for high-density deployments, 60A/208V',
    specs: {
      outlets: 36,
      inputVoltage: 208,
      maxAmps: 60,
      powerWatts: 0,
      rackUnits: 0,
      widthMm: 76,
      depthMm: 1778,
      price: 1200,
    },
    mountType: 'rack',
  },

  // ============================================
  // UPS (Uninterruptible Power Supply)
  // ============================================
  {
    id: 'ups-1500va',
    type: 'ups',
    name: 'UPS 1500VA Line-Interactive',
    description: '1500VA/900W line-interactive UPS, 2U rack-mount',
    specs: {
      capacityVA: 1500,
      runtimeMinutes: 10,
      powerWatts: 50,
      rackUnits: 2,
      widthMm: 432,
      depthMm: 508,
      price: 400,
    },
    mountType: 'rack',
  },
  {
    id: 'ups-3000va',
    type: 'ups',
    name: 'UPS 3000VA Online Double-Conversion',
    description: '3000VA/2700W online double-conversion UPS, 3U rack-mount',
    specs: {
      capacityVA: 3000,
      runtimeMinutes: 8,
      powerWatts: 100,
      rackUnits: 3,
      widthMm: 432,
      depthMm: 660,
      price: 1200,
    },
    mountType: 'rack',
  },
  {
    id: 'ups-6000va',
    type: 'ups',
    name: 'UPS 6000VA Online Double-Conversion',
    description: '6000VA/5400W online double-conversion UPS, floor-standing',
    specs: {
      capacityVA: 6000,
      runtimeMinutes: 12,
      powerWatts: 200,
      widthMm: 440,
      depthMm: 680,
      heightMm: 880,
      price: 3500,
    },
    mountType: 'floor',
  },
  {
    id: 'ups-10000va',
    type: 'ups',
    name: 'UPS 10kVA Modular',
    description: '10kVA/9kW modular UPS with N+1 redundancy',
    specs: {
      capacityVA: 10000,
      runtimeMinutes: 15,
      powerWatts: 350,
      widthMm: 440,
      depthMm: 780,
      heightMm: 1330,
      price: 8000,
    },
    mountType: 'floor',
  },

  // ============================================
  // COOLING
  // ============================================
  {
    id: 'cooling-portable-12k',
    type: 'cooling',
    name: 'Portable AC (12,000 BTU)',
    description: 'Portable spot cooler for supplemental cooling',
    specs: {
      btuPerHour: 12000,
      cfm: 400,
      powerWatts: 1200,
      widthMm: 450,
      depthMm: 400,
      heightMm: 900,
      price: 600,
    },
    mountType: 'floor',
  },
  {
    id: 'cooling-portable-24k',
    type: 'cooling',
    name: 'Portable AC (24,000 BTU)',
    description: 'High-capacity portable spot cooler',
    specs: {
      btuPerHour: 24000,
      cfm: 800,
      powerWatts: 2400,
      widthMm: 550,
      depthMm: 500,
      heightMm: 1100,
      price: 1200,
    },
    mountType: 'floor',
  },
  {
    id: 'cooling-inrow-20kw',
    type: 'cooling',
    name: 'In-Row Cooling Unit (20kW)',
    description: 'Rack-style in-row cooling unit, 68,000 BTU/hr',
    specs: {
      btuPerHour: 68000,
      cfm: 2000,
      powerWatts: 5000,
      rackUnits: 42,
      widthMm: 300,
      depthMm: 1070,
      price: 8000,
    },
    mountType: 'floor',
  },
  {
    id: 'cooling-split-30kw',
    type: 'cooling',
    name: 'Split System (30kW)',
    description: 'Wall-mount split system with external condenser',
    specs: {
      btuPerHour: 102000,
      cfm: 3000,
      powerWatts: 8000,
      widthMm: 1200,
      depthMm: 300,
      heightMm: 600,
      price: 12000,
    },
    mountType: 'wall',
  },
  {
    id: 'cooling-container-crac',
    type: 'cooling',
    name: 'Container CRAC Unit (50kW)',
    description: 'Computer Room AC designed for containers, 170,000 BTU/hr',
    specs: {
      btuPerHour: 170000,
      cfm: 5000,
      powerWatts: 15000,
      widthMm: 800,
      depthMm: 900,
      heightMm: 2000,
      price: 25000,
    },
    mountType: 'floor',
  },

  // ============================================
  // FIRE SUPPRESSION
  // ============================================
  {
    id: 'fire-fm200-rack',
    type: 'fire-suppression',
    name: 'FM-200 Rack Unit',
    description: 'In-rack FM-200 fire suppression, 2U',
    specs: {
      coverageAreaM2: 10,
      powerWatts: 0,
      rackUnits: 2,
      widthMm: 432,
      depthMm: 500,
      price: 2500,
    },
    mountType: 'rack',
  },
  {
    id: 'fire-fm200-container',
    type: 'fire-suppression',
    name: 'Container Fire System',
    description: 'Full container FM-200 suppression system',
    specs: {
      coverageAreaM2: 30,
      powerWatts: 50,
      widthMm: 400,
      depthMm: 400,
      heightMm: 1500,
      price: 8000,
    },
    mountType: 'floor',
  },
  {
    id: 'fire-novec-container',
    type: 'fire-suppression',
    name: 'Novec 1230 System',
    description: 'Environmentally-friendly Novec 1230 suppression',
    specs: {
      coverageAreaM2: 30,
      powerWatts: 50,
      widthMm: 500,
      depthMm: 500,
      heightMm: 1800,
      price: 12000,
    },
    mountType: 'floor',
  },

  // ============================================
  // CABLE MANAGEMENT
  // ============================================
  {
    id: 'cable-vertical-manager',
    type: 'cable-tray',
    name: 'Vertical Cable Manager',
    description: 'Full-height vertical cable organizer',
    specs: {
      powerWatts: 0,
      rackUnits: 42,
      widthMm: 150,
      depthMm: 300,
      price: 150,
    },
    mountType: 'rack',
  },
  {
    id: 'cable-horizontal-1u',
    type: 'cable-tray',
    name: 'Horizontal Cable Manager 1U',
    description: '1U horizontal cable organizer with brush strip',
    specs: {
      powerWatts: 0,
      rackUnits: 1,
      widthMm: 482,
      depthMm: 100,
      price: 35,
    },
    mountType: 'rack',
  },
  {
    id: 'cable-overhead-tray',
    type: 'cable-tray',
    name: 'Overhead Cable Tray (per meter)',
    description: 'Ceiling-mount cable ladder, price per meter',
    specs: {
      powerWatts: 0,
      widthMm: 300,
      depthMm: 100,
      heightMm: 100,
      price: 80,
    },
    mountType: 'ceiling',
  },

  // ============================================
  // MONITORING
  // ============================================
  {
    id: 'monitor-env-sensor',
    type: 'monitoring',
    name: 'Environmental Sensor',
    description: 'Temperature, humidity, and airflow sensor',
    specs: {
      powerWatts: 5,
      widthMm: 100,
      depthMm: 60,
      heightMm: 30,
      price: 200,
    },
    mountType: 'rack',
  },
  {
    id: 'monitor-water-leak',
    type: 'monitoring',
    name: 'Water Leak Detector',
    description: 'Floor-level water/moisture detection system',
    specs: {
      powerWatts: 2,
      widthMm: 200,
      depthMm: 50,
      heightMm: 20,
      price: 150,
    },
    mountType: 'floor',
  },
  {
    id: 'monitor-camera',
    type: 'monitoring',
    name: 'Security Camera System',
    description: 'IP camera with NVR storage',
    specs: {
      powerWatts: 25,
      widthMm: 150,
      depthMm: 150,
      heightMm: 100,
      price: 500,
    },
    mountType: 'ceiling',
  },
  {
    id: 'monitor-access-control',
    type: 'monitoring',
    name: 'Access Control Panel',
    description: 'Biometric/card access control system',
    specs: {
      powerWatts: 15,
      widthMm: 300,
      depthMm: 100,
      heightMm: 200,
      price: 800,
    },
    mountType: 'wall',
  },
  {
    id: 'monitor-kvm',
    type: 'monitoring',
    name: 'KVM Console (1U)',
    description: '8-port KVM with LCD console',
    specs: {
      powerWatts: 30,
      rackUnits: 1,
      widthMm: 482,
      depthMm: 500,
      price: 600,
    },
    mountType: 'rack',
  },

  // ============================================
  // WORKSPACE (Desk, Monitors, Workstation)
  // ============================================
  {
    id: 'workspace-desk-compact',
    type: 'workspace',
    name: 'Compact Workstation Desk',
    description: 'Space-efficient operator desk (1200mm x 600mm)',
    specs: {
      powerWatts: 0,
      widthMm: 1200,
      depthMm: 600,
      heightMm: 750,
      price: 350,
      hasKeyboard: true,
      ergonomic: false,
    },
    mountType: 'floor',
  },
  {
    id: 'workspace-desk-full',
    type: 'workspace',
    name: 'Full Operator Desk',
    description: 'Full-size sit-stand desk with cable management (1600mm x 800mm)',
    specs: {
      powerWatts: 50, // Motorized sit-stand
      widthMm: 1600,
      depthMm: 800,
      heightMm: 750,
      price: 800,
      hasKeyboard: true,
      ergonomic: true,
    },
    mountType: 'floor',
  },
  {
    id: 'workspace-monitor-24',
    type: 'workspace',
    name: 'Monitor 24" (Single)',
    description: '24" 1080p operator display with VESA mount',
    specs: {
      powerWatts: 30,
      widthMm: 550,
      depthMm: 200,
      heightMm: 400,
      price: 200,
      monitorCount: 1,
      monitorSizeInch: 24,
    },
    mountType: 'floor',
  },
  {
    id: 'workspace-monitor-27-dual',
    type: 'workspace',
    name: 'Dual Monitor Setup 27"',
    description: 'Two 27" 4K displays with dual-arm mount',
    specs: {
      powerWatts: 80,
      widthMm: 1250,
      depthMm: 200,
      heightMm: 500,
      price: 900,
      monitorCount: 2,
      monitorSizeInch: 27,
    },
    mountType: 'floor',
  },
  {
    id: 'workspace-monitor-wall-32',
    type: 'workspace',
    name: 'Wall Monitor 32"',
    description: '32" 4K wall-mounted display for dashboards',
    specs: {
      powerWatts: 60,
      widthMm: 730,
      depthMm: 80,
      heightMm: 430,
      price: 450,
      monitorCount: 1,
      monitorSizeInch: 32,
    },
    mountType: 'wall',
  },
  {
    id: 'workspace-chair',
    type: 'workspace',
    name: 'Ergonomic Operator Chair',
    description: 'Adjustable ergonomic chair with lumbar support',
    specs: {
      powerWatts: 0,
      widthMm: 650,
      depthMm: 650,
      heightMm: 1200,
      price: 400,
      ergonomic: true,
    },
    mountType: 'floor',
  },

  // ============================================
  // INSULATION (Thermal & Acoustic)
  // ============================================
  {
    id: 'insulation-wall-foam',
    type: 'insulation',
    name: 'Wall Insulation Panel (Foam)',
    description: 'Closed-cell foam insulation panel (per m²)',
    specs: {
      powerWatts: 0,
      widthMm: 1000,
      depthMm: 50,
      heightMm: 1000,
      price: 45,
      rValue: 3.8,
      soundReductionDb: 15,
      materialType: 'foam',
    },
    mountType: 'wall',
  },
  {
    id: 'insulation-wall-mineral',
    type: 'insulation',
    name: 'Wall Insulation Panel (Mineral Wool)',
    description: 'Fire-resistant mineral wool insulation (per m²)',
    specs: {
      powerWatts: 0,
      widthMm: 1000,
      depthMm: 75,
      heightMm: 1000,
      price: 65,
      rValue: 5.2,
      soundReductionDb: 25,
      materialType: 'mineral-wool',
    },
    mountType: 'wall',
  },
  {
    id: 'insulation-roof-reflective',
    type: 'insulation',
    name: 'Roof Reflective Barrier',
    description: 'Reflective radiant barrier for roof (per m²)',
    specs: {
      powerWatts: 0,
      widthMm: 1200,
      depthMm: 10,
      heightMm: 1000,
      price: 25,
      rValue: 1.5,
      soundReductionDb: 5,
      materialType: 'reflective',
    },
    mountType: 'ceiling',
  },
  {
    id: 'insulation-floor-composite',
    type: 'insulation',
    name: 'Floor Insulation (Composite)',
    description: 'Composite floor insulation with vapor barrier (per m²)',
    specs: {
      powerWatts: 0,
      widthMm: 1000,
      depthMm: 60,
      heightMm: 1000,
      price: 80,
      rValue: 4.5,
      soundReductionDb: 20,
      materialType: 'composite',
    },
    mountType: 'floor',
  },
  {
    id: 'insulation-acoustic-panel',
    type: 'insulation',
    name: 'Acoustic Absorption Panel',
    description: 'Sound-absorbing wall panel for noise reduction',
    specs: {
      powerWatts: 0,
      widthMm: 600,
      depthMm: 50,
      heightMm: 1200,
      price: 120,
      rValue: 1.0,
      soundReductionDb: 35,
      materialType: 'foam',
    },
    mountType: 'wall',
  },

  // ============================================
  // STRUCTURAL (Partitions, Frame Adaptations)
  // ============================================
  {
    id: 'structural-partition-solid',
    type: 'structural',
    name: 'Partition Wall (Solid)',
    description: 'Full-height solid partition wall (per linear meter)',
    specs: {
      powerWatts: 0,
      widthMm: 1000,
      depthMm: 100,
      heightMm: 2350,
      price: 400,
      loadCapacityKg: 50,
      adjustable: false,
      partitionType: 'solid',
      structuralType: 'wall',
      allowsPlacement: false,
      heightType: 'full',
    },
    mountType: 'floor',
  },
  {
    id: 'structural-partition-half',
    type: 'structural',
    name: 'Half-Height Partition',
    description: 'Half-height partition wall (per linear meter)',
    specs: {
      powerWatts: 0,
      widthMm: 1000,
      depthMm: 100,
      heightMm: 1175,
      price: 250,
      loadCapacityKg: 30,
      adjustable: false,
      partitionType: 'solid',
      structuralType: 'partition',
      allowsPlacement: true,
      heightType: 'half',
    },
    mountType: 'floor',
  },
  {
    id: 'structural-partition-glass',
    type: 'structural',
    name: 'Partition Wall (Glass)',
    description: 'Full-height glass partition with frame (per linear meter)',
    specs: {
      powerWatts: 0,
      widthMm: 1000,
      depthMm: 80,
      heightMm: 2350,
      price: 650,
      loadCapacityKg: 20,
      adjustable: false,
      partitionType: 'glass',
      structuralType: 'wall',
      allowsPlacement: false,
      heightType: 'full',
    },
    mountType: 'floor',
  },
  {
    id: 'structural-partition-modular',
    type: 'structural',
    name: 'Modular Partition System',
    description: 'Reconfigurable modular partition (per 2m section)',
    specs: {
      powerWatts: 0,
      widthMm: 2000,
      depthMm: 120,
      heightMm: 2350,
      price: 550,
      loadCapacityKg: 30,
      adjustable: true,
      partitionType: 'modular',
    },
    mountType: 'floor',
  },
  {
    id: 'structural-partition-mesh',
    type: 'structural',
    name: 'Security Mesh Partition',
    description: 'Wire mesh security partition (per linear meter)',
    specs: {
      powerWatts: 0,
      widthMm: 1000,
      depthMm: 50,
      heightMm: 2350,
      price: 280,
      loadCapacityKg: 100,
      adjustable: false,
      partitionType: 'mesh',
    },
    mountType: 'floor',
  },
  {
    id: 'structural-door-personnel',
    type: 'structural',
    name: 'Personnel Door',
    description: 'Standard personnel access door with frame',
    specs: {
      powerWatts: 0,
      widthMm: 900,
      depthMm: 100,
      heightMm: 2100,
      price: 450,
      loadCapacityKg: 0,
      adjustable: false,
      partitionType: 'solid',
    },
    mountType: 'floor',
  },
  {
    id: 'structural-frame-brace',
    type: 'structural',
    name: 'Adaptable Frame Brace',
    description: 'Adjustable structural brace for container reinforcement',
    specs: {
      powerWatts: 0,
      widthMm: 100,
      depthMm: 100,
      heightMm: 2350,
      price: 180,
      loadCapacityKg: 500,
      adjustable: true,
    },
    mountType: 'floor',
  },
  {
    id: 'structural-frame-rail',
    type: 'structural',
    name: 'Equipment Mounting Rail',
    description: 'Wall-mount equipment rail with adjustable brackets',
    specs: {
      powerWatts: 0,
      widthMm: 2000,
      depthMm: 80,
      heightMm: 60,
      price: 120,
      loadCapacityKg: 150,
      adjustable: true,
    },
    mountType: 'wall',
  },
  {
    id: 'structural-raised-floor',
    type: 'structural',
    name: 'Raised Floor Section',
    description: 'Modular raised floor panel for cable routing (per m²)',
    specs: {
      powerWatts: 0,
      widthMm: 600,
      depthMm: 600,
      heightMm: 150,
      price: 95,
      loadCapacityKg: 1000,
      adjustable: false,
      structuralType: 'raised-floor',
      allowsPlacement: true,
    },
    mountType: 'floor',
  },
  {
    id: 'structural-raised-floor-high',
    type: 'structural',
    name: 'Raised Floor (High Profile)',
    description: 'High raised floor for larger cable runs (per m²)',
    specs: {
      powerWatts: 0,
      widthMm: 600,
      depthMm: 600,
      heightMm: 300,
      price: 145,
      loadCapacityKg: 1200,
      adjustable: true,
      structuralType: 'raised-floor',
      allowsPlacement: true,
    },
    mountType: 'floor',
  },
  {
    id: 'structural-shelf-wall',
    type: 'structural',
    name: 'Wall-Mount Shelf (Heavy Duty)',
    description: 'Heavy-duty wall shelf for equipment storage (600mm x 400mm)',
    specs: {
      powerWatts: 0,
      widthMm: 600,
      depthMm: 400,
      heightMm: 50,
      price: 85,
      loadCapacityKg: 75,
      adjustable: true,
      structuralType: 'shelf',
      allowsPlacement: true,
    },
    mountType: 'wall',
  },
  {
    id: 'structural-shelf-unit',
    type: 'structural',
    name: 'Freestanding Shelf Unit',
    description: '4-tier industrial shelving unit (1200mm x 500mm)',
    specs: {
      powerWatts: 0,
      widthMm: 1200,
      depthMm: 500,
      heightMm: 1800,
      price: 250,
      loadCapacityKg: 200,
      adjustable: true,
      structuralType: 'shelf',
      allowsPlacement: true,
    },
    mountType: 'floor',
  },
  {
    id: 'structural-shelf-compact',
    type: 'structural',
    name: 'Compact Storage Shelf',
    description: '3-tier compact shelving for small items (800mm x 400mm)',
    specs: {
      powerWatts: 0,
      widthMm: 800,
      depthMm: 400,
      heightMm: 1200,
      price: 150,
      loadCapacityKg: 100,
      adjustable: true,
    },
    mountType: 'floor',
  },
  {
    id: 'structural-shelf-corner',
    type: 'structural',
    name: 'Corner Shelf Unit',
    description: 'Space-saving corner shelving unit',
    specs: {
      powerWatts: 0,
      widthMm: 600,
      depthMm: 600,
      heightMm: 1500,
      price: 180,
      loadCapacityKg: 80,
      adjustable: true,
    },
    mountType: 'floor',
  },
];

// ============================================
// CONTAINER INSULATION PRESETS
// ============================================
import { ContainerInsulation, ContainerSize } from '../types/configurator';

export const CONTAINER_INSULATION_PRESETS: Record<string, Omit<ContainerInsulation, 'enabled'>> = {
  none: {
    type: 'none',
    estimatedCost: 0,
  },
  basic: {
    type: 'basic',
    wallRValue: 5,
    ceilingRValue: 5,
    floorRValue: 3,
    soundReductionDb: 10,
    estimatedCost: 1500,
  },
  thermal: {
    type: 'thermal',
    wallRValue: 13,
    ceilingRValue: 19,
    floorRValue: 10,
    soundReductionDb: 15,
    estimatedCost: 3500,
  },
  acoustic: {
    type: 'acoustic',
    wallRValue: 8,
    ceilingRValue: 8,
    floorRValue: 5,
    soundReductionDb: 40,
    estimatedCost: 4000,
  },
  combo: {
    type: 'combo',
    wallRValue: 19,
    ceilingRValue: 25,
    floorRValue: 13,
    soundReductionDb: 50,
    estimatedCost: 6500,
  },
};

// Calculate insulation cost based on container size
export function calculateInsulationCost(
  insulationType: ContainerInsulation['type'],
  containerSize: ContainerSize
): number {
  const preset = CONTAINER_INSULATION_PRESETS[insulationType];
  if (!preset) return 0;
  // 40ft container costs ~1.8x more due to larger area
  const multiplier = containerSize === '40ft' ? 1.8 : 1.0;
  return Math.round(preset.estimatedCost * multiplier);
}

// Helper functions
export function getInfrastructureById(id: string): InfrastructureCatalogItem | undefined {
  return INFRASTRUCTURE_CATALOG.find(item => item.id === id);
}

export function getInfrastructureByType(type: InfrastructureType): InfrastructureCatalogItem[] {
  return INFRASTRUCTURE_CATALOG.filter(item => item.type === type);
}

export function getInfrastructureCategories(): { type: InfrastructureType; label: string }[] {
  return [
    { type: 'pdu', label: 'Power Distribution' },
    { type: 'ups', label: 'Uninterruptible Power' },
    { type: 'cooling', label: 'Cooling' },
    { type: 'fire-suppression', label: 'Fire Suppression' },
    { type: 'cable-tray', label: 'Cable Management' },
    { type: 'monitoring', label: 'Monitoring' },
    { type: 'workspace', label: 'Workspace' },
    { type: 'insulation', label: 'Insulation' },
    { type: 'structural', label: 'Structural' },
  ];
}

// Infrastructure icons (for use with lucide-react)
export const INFRASTRUCTURE_ICONS: Record<InfrastructureType, string> = {
  'pdu': 'Plug',
  'ups': 'Battery',
  'cooling': 'Snowflake',
  'fire-suppression': 'Flame',
  'cable-tray': 'Cable',
  'monitoring': 'Eye',
  'workspace': 'Monitor',
  'insulation': 'Layers',
  'structural': 'Construction',
};

// Infrastructure colors
export const INFRASTRUCTURE_COLORS: Record<InfrastructureType, { bg: string; border: string; text: string }> = {
  'pdu': { bg: 'bg-yellow-100', border: 'border-yellow-400', text: 'text-yellow-700' },
  'ups': { bg: 'bg-emerald-100', border: 'border-emerald-400', text: 'text-emerald-700' },
  'cooling': { bg: 'bg-cyan-100', border: 'border-cyan-400', text: 'text-cyan-700' },
  'fire-suppression': { bg: 'bg-red-100', border: 'border-red-400', text: 'text-red-700' },
  'cable-tray': { bg: 'bg-gray-100', border: 'border-gray-400', text: 'text-gray-700' },
  'monitoring': { bg: 'bg-indigo-100', border: 'border-indigo-400', text: 'text-indigo-700' },
  'workspace': { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-700' },
  'insulation': { bg: 'bg-orange-100', border: 'border-orange-400', text: 'text-orange-700' },
  'structural': { bg: 'bg-slate-100', border: 'border-slate-400', text: 'text-slate-700' },
};
